import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { createTenant } from '../store/slices/tenantSlice';
import { addNotification } from '../store/slices/uiSlice';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { FiSave, FiX, FiArrowLeft, FiPlus, FiTrash2 } from 'react-icons/fi';
import { fetchClusters } from '../store/slices/clusterSlice';
import { TenantCreate, provisionTenant } from '../api/tenantApi';
 
// Define types for clusters
interface Cluster {
    _id: string;
    clusterId: string;
}

// Validation schema
const TenantSchema = Yup.object().shape({
    // Every per-tenant secret is named `{tenantId}-serviceAuthentication-{service}-{slot}-privateKey`
    // and an Azure Key Vault secret name permits ONLY alphanumerics and dashes, max 127 chars.
    // The underscore this pattern used to allow is why `aio_test` can never have signing keys:
    // the vault rejects the name outright. 66 = 127 minus the longest suffix the platform builds
    // (`-serviceAuthentication-contracts-provider-previous-privateKey`, 61 chars); the server
    // computes the same number from constants/serviceKeys.ts and is the authority.
    tenantId: Yup.string()
        .matches(/^[0-9a-zA-Z-]+$/, "Only letters, digits and '-' are allowed \u2014 Azure Key Vault rejects any other character in a secret name, so this tenant's signing keys could never be created")
        .max(66, 'Too long: an Azure Key Vault secret name is capped at 127 characters and the longest per-tenant suffix is 61'),
    name: Yup.string()
        .required('Name is required'),
    url: Yup.string()
        .required('URL is required')
        .matches(/^[a-z0-9]([a-z0-9-]+[a-z0-9])?(\.[a-z0-9]([a-z0-9-]+[a-z0-9])?)*$/, 'Enter a valid URL'),
    databaseName: Yup.string().required('Database name is required'),
    cluster: Yup.string().required('Cluster is required'),
    createDnsEntry: Yup.boolean(),
    createK8sIngress: Yup.boolean(),
    status: Yup.string().oneOf(['active', 'disabled', 'maintenance']),
    authenticationProviders: Yup.array().of(
        Yup.object().shape({
            type: Yup.string().required('Provider type is required'),
            providerId: Yup.string().required('Provider ID is required'),
            label: Yup.string().required('Label is required'),
        })
    ),
});

const TenantCreatePage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { loadingTenant, error } = useSelector((state: RootState) => state.tenants);
    const [showKcAdvanced, setShowKcAdvanced] = useState(false);
    const {
        clusters,
        loadingCluster: clustersLoading,
        error: clustersError,
    } = useSelector((state: RootState) => state.clusters);

    // A failed GET /api/clusters and a genuinely empty Clusters collection must never look
    // the same. This dropdown used to fall back to a hardcoded "DevCluster" option whenever
    // the list was empty for any reason, so a rejected request let a tenant be submitted
    // against a cluster nobody had confirmed exists.
    const loadClusters = useCallback(() => {
        dispatch(fetchClusters());
    }, [dispatch]);

    useEffect(() => {
        loadClusters();
    }, [loadClusters]);

    const clustersState: 'loading' | 'error' | 'ok' = clustersLoading
        ? 'loading'
        : clustersError
            ? 'error'
            : 'ok';
    const clustersReady = clustersState === 'ok' && clusters.length > 0;

    const initialValues = {
        tenantId: '',
        name: '',
        url: '',
        createDnsEntry: true,
        createK8sIngress: true,

        nodeSelector: { key: 'tenant', value: '' },
        serviceName: 'tenant-service',
        servicePort: 80,

        // Deployment target. Sent to /provision, which derives the namespace, the
        // environmentTag and the secret store from it.
        environment: 'dev' as 'prod' | 'dev' | 'demo',

        databaseName: 'DataStore',
        // Deliberately blank: the only valid values are the ones GET /api/clusters returned.
        // (Formik has no enableReinitialize here, so anything derived from `clusters` would be
        // frozen at first render anyway - when the list was still empty.)
        cluster: '',
        status: 'active',

        // OIDC providers (stored in authenticationProviders with type='oidc')
        oidcProviders: [] as any[],
        // External login methods (OTP / magic link) — NOT in authenticationProviders,
        // controlled via keycloak.clients.externalOtp / externalMagicLink
        authOtp: true,
        authMagicLink: true,
        // Federated AD helper — if filled, added as an oidc entry automatically
        authFederatedAd: false,
        federatedAd: {
            alias: 'microsoft',
            displayName: 'Microsoft',
            clientId: '',
            clientSecret: '',
            azureTenantId: '',
            defaultScopes: 'openid email profile',
        },

        // BFF connection fields
        useSsl: true,
        port: null as number | null,

        // Provisioning strategy
        provisioningInternalMode: 'AutoProvision' as 'AutoProvision' | 'InviteOnly' | 'Disabled',
        provisioningExternalMode: 'AutoProvision' as 'AutoProvision' | 'InviteOnly' | 'Disabled',

        // Redis — blank means "use the shared platform ACL user" (server-side default).
        // NOT auto-generated: there is no per-tenant Redis user.
        redisUsername: '',
        redisPasswordKey: '',

        // Security / JWT
        securityAlgorithm: 'ES256',
        securityEcCurve: 'P-256',
        securityKeyRotationDays: 30,
        securityKeyGracePeriodDays: 3,

        // Optional Keycloak credentials (triggers /provision when filled)
        keycloakAdminUrl: '',
        keycloakPublicUrl: '',
        keycloakAdminUser: 'admin',
        keycloakAdminPassword: '',
        jwksHost: 'identity-provider',
        jwksPort: 8080,
        secretStoreName: '',

        keys: {
            SignatureKeyManager: 'Mongo',
            EncryptionKeyManager: 'Mongo',
        },
        email: {
            sendEmailNotificationAddress: '',
            sendEmailNotifcationDisplayName: '',
            acsEndpoint: null,
        },
    };

    const handleSubmit = async (values: any) => {
        const authMethods = {
            credential: true,
            otp: values.authOtp,
            magicLink: values.authMagicLink,
            federatedAd: values.authFederatedAd && values.federatedAd.clientId ? values.federatedAd : null,
        };

        try {
            // Always call /provision — server uses its service account if no overrides provided.
            // Falls back to MongoDB-only (/create) only if user explicitly wants that.
            if (true) { // keep the block structure for override fields below
                const provisionData = {
                    tenantId: values.tenantId || undefined,
                    displayName: values.name,
                    baseUrl: values.url,
                    protocol: 'https',
                    keycloakAdminUrl: values.keycloakAdminUrl,
                    keycloakPublicUrl: values.keycloakPublicUrl || undefined,
                    keycloakAdminUser: values.keycloakAdminUser,
                    keycloakAdminPassword: values.keycloakAdminPassword,
                    jwksHost: values.jwksHost,
                    jwksPort: values.jwksPort,
                    environment: values.environment,
                    databaseName: values.databaseName,
                    cluster: values.cluster,
                    // Omitted unless the operator explicitly overrode it — the server derives it
                    // from `environment`, which is what keeps a cloud tenant off the local vault.
                    secretStoreName: (values.secretStoreName ?? '').trim() || undefined,
                    emailNotificationAddress: values.email.sendEmailNotificationAddress,
                    emailNotificationDisplayName: values.email.sendEmailNotifcationDisplayName,
                    acsEndpoint: values.email.acsEndpoint || undefined,
                    authMethods,
                    useSsl: values.useSsl,
                    port: values.port ?? null,
                    provisioningInternalMode: values.provisioningInternalMode,
                    provisioningExternalMode: values.provisioningExternalMode,
                    redisUsername: values.redisUsername || undefined,
                    redisPasswordKey: values.redisPasswordKey || undefined,
                    securityAlgorithm: values.securityAlgorithm,
                    securityEcCurve: values.securityEcCurve,
                    securityKeyRotationDays: values.securityKeyRotationDays,
                    securityKeyGracePeriodDays: values.securityKeyGracePeriodDays,
                };

                const resp = await provisionTenant(provisionData);

                if (resp.success) {
                    dispatch(addNotification({
                        type: resp.data ? 'success' : 'warning',
                        message: resp.message ?? 'Tenant provisioned!',
                    }));
                    navigate(`/tenants/${(resp.data as any)._id ?? ''}`);
                }
                return;
            }

            // No KC credentials → MongoDB-only create
            const tenantData: TenantCreate = {
                tenantId: values.tenantId || undefined,
                name: values.name,
                subdomain: values.url,
                databaseName: values.databaseName,
                cluster: values.cluster,
                status: values.status,
                authenticationProviders: buildProvidersFromValues(authMethods),
                authMethods,
                keys: values.keys,
                email: values.email,
                createDnsEntry: values.createDnsEntry,
            };

            const result = await dispatch(createTenant(tenantData)).unwrap();
            dispatch(addNotification({ type: 'success', message: `Tenant "${result.tenantId}" created successfully!` }));
            navigate(`/tenants/${result._id}`);
        } catch (error: any) {
            dispatch(addNotification({
                type: 'error',
                title: 'Tenant Creation Failed',
                message: error.message || 'Failed to create tenant.',
            }));
        }
    };

    function buildProvidersFromValues(auth: any) {
        const providers: any[] = [
            { type: 'credential', providerId: 'credential', label: 'Sign in', fields: { password: { label: 'Password', type: 'Password' } } },
        ];
        if (auth.otp) providers.push({ type: 'otp', providerId: 'ontoris-external-otp', label: 'Email OTP' });
        if (auth.magicLink) providers.push({ type: 'magic-link', providerId: 'ontoris-external', label: 'Magic Link' });
        if (auth.federatedAd) providers.push({ type: 'federated-ad', providerId: auth.federatedAd.alias, label: auth.federatedAd.displayName, ...auth.federatedAd });
        return providers;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center">
                <button
                    onClick={() => navigate('/tenants')}
                    className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                    <FiArrowLeft className="mr-2" />
                    Back to Tenants
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Create New Tenant</h1>
            </div>

            {error && (
                <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-600 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
                <Formik
                    initialValues={initialValues}
                    validationSchema={TenantSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, isValid, dirty }) => (
                        <Form className="p-6 space-y-6">
                            {/* Basic Tenant Information Section */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Basic Information
                                </h3>

                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-6">
                                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Name
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="name"
                                                type="text"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            />
                                        </div>
                                        <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>

                                    <div className="sm:col-span-3">
                                        <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Tenant ID (Leave empty for auto-generated GUID)
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="tenantId"
                                                type="text"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                placeholder="Auto-generated if empty"
                                            />
                                        </div>
                                        <ErrorMessage name="tenantId" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>

                                    <div className="sm:col-span-3">
                                        <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Initial Status
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                as="select"
                                                name="status"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            >
                                                <option value="active">Active</option>
                                                <option value="disabled">Disabled</option>
                                                <option value="maintenance">Maintenance</option>
                                            </Field>
                                        </div>
                                        <ErrorMessage name="status" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                            </div>

                            {/* URL, DNS, and Ingress Configuration Section */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    URL & Access Configuration
                                </h3>

                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-6">
                                        <label htmlFor="url" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            URL
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="url"
                                                type="text"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                placeholder="e.g. tenant-name.example.com"
                                            />
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            This URL will be used for both DNS entry and Kubernetes Ingress if enabled.
                                        </p>
                                        <ErrorMessage name="url" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>

                                    <div className="sm:col-span-3">
                                        <div className="relative flex items-start">
                                            <div className="flex items-center h-5">
                                                <Field
                                                    type="checkbox"
                                                    name="createDnsEntry"
                                                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor="createDnsEntry" className="font-medium text-gray-700 dark:text-gray-300">
                                                    Create DNS Entry
                                                </label>
                                                <p className="text-gray-500 dark:text-gray-400">
                                                    Creates a DNS entry for the URL
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-3">
                                        <div className="relative flex items-start">
                                            <div className="flex items-center h-5">
                                                <Field
                                                    type="checkbox"
                                                    name="createK8sIngress"
                                                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 dark:border-gray-600 rounded"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor="createK8sIngress" className="font-medium text-gray-700 dark:text-gray-300">
                                                    Create Kubernetes Ingress
                                                </label>
                                                <p className="text-gray-500 dark:text-gray-400">
                                                    Creates an Nginx ingress with Let's Encrypt SSL certificate
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {values.createK8sIngress && (
                                        <div className="sm:col-span-6">
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                                                    Kubernetes Ingress Configuration
                                                </h4>

                                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                                    <div className="sm:col-span-3">
                                                        <label htmlFor="nodeSelector.key" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Node Selector Key
                                                        </label>
                                                        <div className="mt-1">
                                                            <Field
                                                                name="nodeSelector.key"
                                                                type="text"
                                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                placeholder="e.g., tenant"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="nodeSelector.key" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                    </div>

                                                    <div className="sm:col-span-3">
                                                        <label htmlFor="nodeSelector.value" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Node Selector Value
                                                        </label>
                                                        <div className="mt-1">
                                                            <Field
                                                                name="nodeSelector.value"
                                                                type="text"
                                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                placeholder="e.g., tenant1"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="nodeSelector.value" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                    </div>

                                                    <div className="sm:col-span-3">
                                                        <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Service Name
                                                        </label>
                                                        <div className="mt-1">
                                                            <Field
                                                                name="serviceName"
                                                                type="text"
                                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                placeholder="e.g., tenant-service"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="serviceName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                    </div>

                                                    <div className="sm:col-span-3">
                                                        <label htmlFor="servicePort" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Service Port
                                                        </label>
                                                        <div className="mt-1">
                                                            <Field
                                                                name="servicePort"
                                                                type="number"
                                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                                                placeholder="e.g., 80"
                                                            />
                                                        </div>
                                                        <ErrorMessage name="servicePort" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Deployment target, database and cluster */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Deployment Target, Database & Cluster
                                </h3>

                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-3">
                                        <label htmlFor="environment" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Environment
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                as="select"
                                                id="environment"
                                                name="environment"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            >
                                                <option value="dev">Development</option>
                                                <option value="demo">Demo</option>
                                                <option value="prod">Production</option>
                                            </Field>
                                        </div>
                                        <p className="mt-1 text-xs text-gray-400">
                                            Deployment target. The server derives the namespace, the environment tag the platform
                                            filters tenants by, and the Key Vault this tenant&apos;s services read secrets from.
                                        </p>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label htmlFor="databaseName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Database Name
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="databaseName"
                                                type="text"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            />
                                        </div>
                                        <ErrorMessage name="databaseName" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>

                                    <div className="sm:col-span-3">
                                        <label htmlFor="cluster" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Cluster
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                as="select"
                                                name="cluster"
                                                disabled={!clustersReady}
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md disabled:opacity-60"
                                            >
                                                <option value="">
                                                    {clustersState === 'loading'
                                                        ? 'Loading clusters…'
                                                        : clustersState === 'error'
                                                            ? '— Cluster list unavailable —'
                                                            : clusters.length === 0
                                                                ? '— No clusters defined —'
                                                                : '— Select a cluster —'}
                                                </option>
                                                {clusters.map((cluster) => (
                                                    <option key={cluster._id} value={cluster.clusterId}>
                                                        {cluster.clusterId}
                                                    </option>
                                                ))}
                                            </Field>
                                        </div>
                                        {clustersState === 'error' && (
                                            <div className="mt-2 p-2 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/40 text-xs text-red-700 dark:text-red-300">
                                                <p>
                                                    Could not load the cluster list: {clustersError}. The list could not be
                                                    verified, so no cluster can be selected. Retry, and if it keeps failing
                                                    reload the page to re-authenticate.
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={loadClusters}
                                                    className="mt-1 underline hover:text-red-900 dark:hover:text-red-200"
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        )}
                                        {clustersState === 'ok' && clusters.length === 0 && (
                                            <p className="mt-2 text-xs text-yellow-700 dark:text-yellow-400">
                                                The request succeeded but the Clusters collection returned no entries — add a
                                                cluster under Db Clusters before creating a tenant.
                                            </p>
                                        )}
                                        <ErrorMessage name="cluster" component="div" className="mt-1 text-sm text-red-600 dark:text-red-400" />
                                    </div>
                                </div>
                            </div>

                            {/* OIDC Providers (stored in authenticationProviders) */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">OIDC Providers</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Identity providers for internal users (Azure AD, corporate OIDC, etc.). Stored in <code className="text-xs">authenticationProviders</code>.
                                </p>

                                <FieldArray name="oidcProviders">
                                    {({ push, remove }) => (
                                        <div className="mt-4 space-y-4">
                                            {values.oidcProviders.map((_p: any, i: number) => (
                                                <div key={i} className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 relative">
                                                    <button type="button" onClick={() => remove(i)}
                                                        className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                                                        <FiTrash2 className="h-4 w-4" />
                                                    </button>
                                                    <div className="grid grid-cols-1 gap-y-3 gap-x-4 sm:grid-cols-6">
                                                        {[
                                                            ['Label',       `oidcProviders.${i}.label`,       'e.g. Ontoris Azure'],
                                                            ['Alias',       `oidcProviders.${i}.alias`,       'e.g. microsoft'],
                                                            ['Client ID',   `oidcProviders.${i}.clientId`,   ''],
                                                            ['Client Secret', `oidcProviders.${i}.clientSecret`, ''],
                                                            ['Azure Tenant ID', `oidcProviders.${i}.tenantId`, ''],
                                                            ['Provider Name', `oidcProviders.${i}.providerName`, 'AzureAD'],
                                                        ].map(([label, name, placeholder]) => (
                                                            <div key={name} className="sm:col-span-3">
                                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">{label}</label>
                                                                <Field name={name} type={name.includes('Secret') ? 'password' : 'text'} placeholder={placeholder}
                                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button"
                                                onClick={() => push({ label: '', alias: '', clientId: '', clientSecret: '', tenantId: '', providerName: 'AzureAD', enabled: true })}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50">
                                                <FiPlus className="mr-2 h-4 w-4" /> Add OIDC Provider
                                            </button>
                                        </div>
                                    )}
                                </FieldArray>
                            </div>

                            {/* External Login Methods (OTP + Magic Link — controlled via keycloak.clients) */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">External Login Methods</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Passwordless methods for external users. Controlled by <code className="text-xs">keycloak.clients</code> — not stored in authenticationProviders.
                                </p>
                                <div className="mt-4 space-y-3">
                                    <div className="flex items-start">
                                        <input type="checkbox" checked disabled
                                            className="mt-0.5 h-4 w-4 opacity-60 cursor-not-allowed border-gray-300 rounded" />
                                        <div className="ml-3">
                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Credential Login</span>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Always enabled.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <Field type="checkbox" name="authOtp" className="mt-0.5 h-4 w-4 border-gray-300 rounded" />
                                        <div className="ml-3">
                                            <label htmlFor="authOtp" className="text-sm font-medium text-gray-700 dark:text-gray-300">Email OTP</label>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">6-digit one-time code via email (<code className="text-xs">email-otp-browser</code> flow in Keycloak).</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <Field type="checkbox" name="authMagicLink" className="mt-0.5 h-4 w-4 border-gray-300 rounded" />
                                        <div className="ml-3">
                                            <label htmlFor="authMagicLink" className="text-sm font-medium text-gray-700 dark:text-gray-300">Magic Link</label>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">Single-use login link via email (<code className="text-xs">magic-link-browser</code> flow in Keycloak).</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Keycloak — auto-provisioned by server, no credentials needed */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Keycloak</h3>

                                {/* Status banner */}
                                <div className="mt-2 p-3 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-green-500 flex-shrink-0" />
                                    Keycloak will be provisioned automatically using the server service account
                                    (<code className="text-xs">KEYCLOAK_BASE_URL</code> + <code className="text-xs">KEYCLOAK_ADMIN_CLIENT_ID</code>).
                                    No credentials required in this form.
                                </div>

                                {/* Tenant-level KC settings that belong in the document */}
                                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Secret Store</label>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            Derived by the server from the Environment above — cloud environments use the platform
                                            Key Vault, local/on-prem deployments use the local one. Typing it by hand is what put
                                            three live cloud tenants on the local vault, so it is an Advanced override only.
                                        </p>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">JWKS Host</label>
                                        <Field name="jwksHost" type="text"
                                            className="mt-1 shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                        <p className="mt-1 text-xs text-gray-400">Internal hostname for the identity-provider JWKS endpoint (default: identity-provider).</p>
                                    </div>
                                </div>

                                {/* Advanced / override — collapsed by default */}
                                <div className="mt-4">
                                    <button type="button"
                                        onClick={() => setShowKcAdvanced(v => !v)}
                                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex items-center gap-1">
                                        <span>{showKcAdvanced ? '▼' : '▶'}</span>
                                        Advanced — override server KC settings (leave blank to use server defaults)
                                    </button>

                                    {showKcAdvanced && (
                                        <div className="mt-3 bg-gray-50 dark:bg-gray-700 rounded-md p-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6">
                                            <div className="sm:col-span-3">
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    KC Base URL override <span className="text-gray-400">(default: server KEYCLOAK_BASE_URL)</span>
                                                </label>
                                                <Field name="keycloakAdminUrl" type="text" placeholder="https://auth.example.com"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                            <div className="sm:col-span-3">
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">KC Public URL <span className="text-gray-400">(if different from admin URL)</span></label>
                                                <Field name="keycloakPublicUrl" type="text" placeholder="https://auth.example.com"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                            <div className="sm:col-span-3">
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                                                    Secret store override <span className="text-gray-400">(default: derived from Environment)</span>
                                                </label>
                                                <Field name="secretStoreName" type="text" placeholder="leave blank — derived"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                                <p className="mt-1 text-xs text-gray-400">
                                                    Azure Key Vault name, matched exactly including case. Only for a tenant that genuinely
                                                    has its own vault.
                                                </p>
                                            </div>
                                            <div className="sm:col-span-3">
                                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">JWKS Port</label>
                                                <Field name="jwksPort" type="number"
                                                    className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Provisioning Section */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Provisioning</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Controls how users are created on first login. AutoProvision creates them automatically; InviteOnly requires an admin invitation first.
                                </p>
                                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Internal Users</label>
                                        <Field as="select" name="provisioningInternalMode"
                                            className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                            <option value="AutoProvision">AutoProvision — create on first login</option>
                                            <option value="InviteOnly">InviteOnly — admin must invite first</option>
                                            <option value="Disabled">Disabled</option>
                                        </Field>
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">External Users</label>
                                        <Field as="select" name="provisioningExternalMode"
                                            className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md">
                                            <option value="AutoProvision">AutoProvision — create on first login</option>
                                            <option value="InviteOnly">InviteOnly — admin must invite first</option>
                                            <option value="Disabled">Disabled</option>
                                        </Field>
                                    </div>
                                </div>
                            </div>

                            {/* Redis Section */}
                            <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Redis Session Store</h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Redis ACL credentials used for this tenant's session data.
                                    <strong> Leave both blank</strong> — the tenant then uses the shared platform
                                    Redis ACL user, which is what every working tenant does. Only fill these in if
                                    this deployment has a dedicated ACL user that already exists in Redis and whose
                                    password is already in the vault; a value entered here is written verbatim and
                                    nothing creates the user for you.
                                </p>
                                <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Redis Username <span className="text-gray-400 text-xs">(optional override)</span>
                                        </label>
                                        <Field name="redisUsername" type="text" placeholder="blank = shared platform Redis user"
                                            className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                    </div>
                                    <div className="sm:col-span-3">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Password Key (Vault) <span className="text-gray-400 text-xs">(optional override)</span>
                                        </label>
                                        <Field name="redisPasswordKey" type="text" placeholder="blank = shared platform secret key"
                                            className="mt-1 shadow-sm block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md" />
                                    </div>
                                </div>
                            </div>

                            {/* Email Configuration Section */}
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                    Email Configuration
                                </h3>

                                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 mt-4">
                                    <div className="sm:col-span-3">
                                        <label htmlFor="email.sendEmailNotificationAddress" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Notification Email Address
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="email.sendEmailNotificationAddress"
                                                type="email"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            />
                                        </div>
                                    </div>

                                    <div className="sm:col-span-3">
                                        <label htmlFor="email.sendEmailNotifcationDisplayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Notification Display Name
                                        </label>
                                        <div className="mt-1">
                                            <Field
                                                name="email.sendEmailNotifcationDisplayName"
                                                type="text"
                                                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Form Actions */}
                            <div className="flex justify-end space-x-3 pt-5">
                                <button
                                    type="button"
                                    onClick={() => navigate('/tenants')}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    <FiX className="mr-2" />
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!(isValid && dirty) || loadingTenant}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed"
                                >
                                    {loadingTenant ? (
                                        <>
                                            <svg
                                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <FiSave className="mr-2" />
                                            Create Tenant
                                        </>
                                    )}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default TenantCreatePage;